import { useEffect, useRef } from 'react';
import '@/shared/components/checkbox-row/checkbox-row.scss';

export default function CheckboxRow({
  checked,
  indeterminate,
  onChange,
  onClick,
  disabled,
  label,
  title,
  className,
  style,
  children,
}: {
  checked: boolean;
  /** Shows a dash instead of a check — for a group whose children are only partially selected. */
  indeterminate?: boolean;
  onChange?: (checked: boolean) => void;
  /** Click handler on the underlying input — e.g. to stop propagation when the row sits inside another clickable element. */
  onClick?: (e: React.MouseEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  /** Simple text label — rendered truncated with a title tooltip when it's a string. Omit for a bare checkbox (e.g. row-selection). */
  label?: React.ReactNode;
  /** Tooltip text; defaults to `label` when label is a string. */
  title?: string;
  className?: string;
  style?: React.CSSProperties;
  /** Extra content after the label (icons, trailing badges, etc.) for rows that need more than plain text. */
  children?: React.ReactNode;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const resolvedTitle = title ?? (typeof label === 'string' ? label : undefined);

  // `indeterminate` isn't a JSX-settable attribute — it only exists as a DOM property.
  useEffect(() => {
    if (inputRef.current) inputRef.current.indeterminate = !!indeterminate;
  }, [indeterminate]);

  return (
    <label
      className={`checkbox-row${className ? ` ${className}` : ''}`}
      style={{ cursor: disabled ? 'default' : 'pointer', ...style }}
    >
      <input
        ref={inputRef}
        type="checkbox"
        className="checkbox-row-input"
        checked={checked}
        disabled={disabled}
        onChange={e => onChange?.(e.target.checked)}
        onClick={onClick}
      />
      {label !== undefined &&
        (typeof label === 'string' ? (
          <span
            className="checkbox-row-label checkbox-row-label-truncate"
            style={{ opacity: disabled ? 0.6 : 1 }}
            title={resolvedTitle}
          >
            {label}
          </span>
        ) : (
          <span className="checkbox-row-label" style={{ opacity: disabled ? 0.6 : 1 }}>
            {label}
          </span>
        ))}
      {children}
    </label>
  );
}
